import { env } from "~/config/env";
import { buildSearchUrl } from "../queryUrls";
import { logger } from "~/lib/logger/logger";
import { MIN_SEARCH_QUERY_LENGTH } from "~/utils/search";
import { fetchFromCMS } from "../queryUtils";

/**
 * Search result from CMS Search collection
 */
export interface CMSSearchResult {
  id: number;
  title: string;
  slug: string;
  url: string;
  contentType: string;
  contentTypeLabel: string;
  priority?: number;
  tenant?: number | { id: number; slug: string; name: string };
  meta?: {
    title?: string;
    description?: string;
    image?: number | { id: number; url: string };
  };
  doc?: {
    relationTo: string;
    value: number;
  };
}

/**
 * CMS Search API response shape
 */
export interface CMSSearchResponse {
  docs: CMSSearchResult[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

/**
 * Empty response helper
 */
function emptyResponse(limit: number = 10): CMSSearchResponse {
  return {
    docs: [],
    totalDocs: 0,
    limit,
    page: 1,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  };
}

/**
 * Server-side query for searching content via CMS Search collection.
 *
 * Uses "use server" (plain, NOT wrapped in query()) so it runs as a simple
 * RPC call. This avoids CORS/GTM fetch-patching issues while NOT triggering
 * SolidRouter's cache invalidation — query() would re-evaluate all createAsync
 * queries and cause navigation to disappear during search loading.
 *
 * Searches the indexed search collection which includes:
 * - Articles, Pages, Podcasts, Podcast Episodes
 * - Channel Pages, Landing Pages, Hosts
 *
 * Results are sorted by priority (higher = more relevant content types)
 * and filtered by title match. Tenant scoping is enforced server-side
 * via the Search collection's access.read — see cms/src/search/tenantScopedAccess.ts.
 *
 * @param searchTerm - The search query string (minimum MIN_SEARCH_QUERY_LENGTH characters)
 * @param limit - Maximum results to return (default: 10)
 * @returns Search results with URLs and content type labels
 */
export async function searchCMSQuery(
  searchTerm: string,
  limit: number = 10
): Promise<CMSSearchResponse> {
  "use server";

  // Validate minimum query length
  if (!searchTerm || searchTerm.trim().length < MIN_SEARCH_QUERY_LENGTH) {
    return emptyResponse(limit);
  }

  const trimmedQuery = searchTerm.trim();

  const url = `${env.cmsApiUrl}${buildSearchUrl({ query: trimmedQuery, tenantSlug: env.tenantSlug, limit })}`;

  const data = await fetchFromCMS<CMSSearchResponse>(url, { logPrefix: "[Search Query]" });

  if (!data?.docs || !Array.isArray(data.docs)) {
    return emptyResponse(limit);
  }

  logger.info(`[Search Query] Found ${data.totalDocs} results for "${trimmedQuery}"`);

  return data;
}
